import type { RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '../stores/authStore'

// CRM Routes - Protected Admin Area
export const createCrmRoutes = (): RouteRecordRaw[] => {
  return [
    {
      path: '/crm',
      name: 'crm-dashboard',
      component: () => import('../views/crm/CrmDashboard.vue'),
      meta: {
        requiresAuth: true,
        requiresRole: ['admin', 'editor'],
        title: 'CRM Dashboard',
      },
      children: [
        {
          path: '',
          name: 'crm-home',
          component: () => import('../views/crm/CrmHome.vue'),
          meta: { title: 'CRM Home' },
        },
        {
          path: 'categories',
          name: 'crm-categories',
          component: () => import('../views/crm/CrmCategories.vue'),
          meta: { title: 'Manage Categories' },
        },
        {
          path: 'categories/new',
          name: 'crm-category-new',
          component: () => import('../views/crm/CrmCategoryEditor.vue'),
          meta: { title: 'New Category' },
        },
        {
          path: 'products',
          name: 'crm-products',
          component: () => import('../views/crm/CrmProducts.vue'),
          meta: { title: 'Manage Products' },
        },
        {
          path: 'products/new',
          name: 'crm-product-new',
          component: () => import('../views/crm/CrmProductEditor.vue'),
          meta: { title: 'New Product' },
        },
        {
          path: 'products/edit/:id',
          name: 'crm-product-edit',
          component: () => import('../views/crm/CrmProductEditor.vue'),
          meta: { title: 'Edit Product' },
        },
        {
          path: 'product-fetcher',
          name: 'crm-product-fetcher',
          component: () => import('../views/crm/CrmProductFetcher.vue'),
          meta: { title: 'Product Fetcher' },
        },
        {
          path: 'amazon-manual',
          name: 'crm-amazon-manual',
          component: () => import('../views/crm/CrmAmazonManual.vue'),
          meta: { title: 'Amazon Manual Entry' },
        },
        {
          path: 'articles',
          name: 'crm-articles',
          component: () => import('../views/crm/EnhancedArticleCRM.vue'),
          meta: { title: 'Article Management Dashboard' },
        },
        {
          path: 'articles/new',
          name: 'crm-article-new',
          component: () => import('../views/crm/CrmArticleEditor.vue'),
          meta: { title: 'New Article' },
        },
        {
          path: 'articles/edit/:id',
          name: 'crm-article-edit',
          component: () => import('../views/crm/CrmArticleEditor.vue'),
          meta: { title: 'Edit Article' },
        },
        {
          path: 'media',
          name: 'crm-media',
          component: () => import('../views/crm/CrmMedia.vue'),
          meta: { title: 'Media Library' },
        },
        {
          path: 'users',
          name: 'crm-users',
          component: () => import('../views/crm/CrmUsers.vue'),
          meta: {
            title: 'User Management',
            requiresRole: ['admin'],
          },
        },
        {
          path: 'settings',
          name: 'crm-settings',
          component: () => import('../views/crm/CrmSettings.vue'),
          meta: {
            title: 'Settings',
            requiresRole: ['admin'],
          },
        },
      ],
    },
  ]
}

// Navigation guard for CRM routes
export const crmGuard = (to: any, from: any, next: any) => {
  const authStore = useAuthStore()

  // Check if user is authenticated
  if (!authStore.isAuthenticated) {
    next({ name: 'sign-in', query: { redirect: to.fullPath } })
    return
  }

  // Check if user has required role
  if (to.meta.requiresRole) {
    const requiredRoles = to.meta.requiresRole
    const userRole = authStore.user?.role

    if (!userRole || !requiredRoles.includes(userRole)) {
      // Redirect to home instead of crm-dashboard to prevent infinite loop
      next({ name: 'home', query: { error: 'insufficient-permissions' } })
      return
    }
  }

  next()
}
